/**
 * API Service für die Kommunikation mit dem refireg-service Backend
 */
import { parseCurrencyInput } from '../utils/formatUtils';

let authContext = null;
let onAuthReadyCallback = null;

export const setAuthContext = (auth) => {
  console.log('setAuthContext called with token:', !!auth?.user?.access_token);
  authContext = auth;
  if (authContext?.user?.access_token && onAuthReadyCallback) {
    console.log('Token available, triggering onAuthReadyCallback');
    onAuthReadyCallback();
    onAuthReadyCallback = null;
  }
};

export const subscribeToAuthReady = (callback) => {
  if (authContext?.user?.access_token) {
    console.log('subscribeToAuthReady: Token already available, executing callback immediately');
    callback();
    return () => {};
  } else {
    console.log('subscribeToAuthReady: Token not available, registering callback');
    onAuthReadyCallback = callback;
    return () => {
      if (onAuthReadyCallback === callback) {
        onAuthReadyCallback = null;
      }
    };
  }
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/refireg-service';

/**
 * Hilfsfunktion für API-Requests
 * @param {string} endpoint
 * @param {Object} options - fetch-Optionen + optionales `responseType: 'blob'`
 */
const fetchAPI = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const { responseType, ...fetchOptions } = options;

  const headers = {
    ...(responseType !== 'blob' && !(fetchOptions.body instanceof FormData) && { 'Content-Type': 'application/json' }),
    ...fetchOptions.headers,
  };

  // Add Authorization header if token is availablesudp
  if (authContext && authContext.user && authContext.user.access_token) {
    headers['Authorization'] = `Bearer ${authContext.user.access_token}`;
  } else {
    console.log(`Abbruch API-Call zu ${endpoint}: Kein Access Token verfügbar. AuthContext vorhanden: ${!!authContext}, User vorhanden: ${!!authContext?.user}`);
    const authError = new Error('Nicht authentifiziert: Kein Access Token verfügbar');
    authError.status = 401;
    throw authError;
  }

  const config = { ...fetchOptions, headers };

  let response;
  try {
    response = await fetch(url, config);
  } catch (error) {
    const networkError = new Error('Netzwerkfehler: Konnte keine Verbindung zum Server herstellen');
    networkError.status = 0;
    networkError.originalError = error;
    throw networkError;
  }

  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    let data = null;
    if (contentType && (contentType.includes('application/json') || contentType.includes('application/problem+json'))) {
      try { data = await response.json(); } catch (_) {}
    }
    console.log('API Error Response:', { status: response.status, statusText: response.statusText, contentType, data });

    let message = data?.detail;
    if (!message && data?.errors && Object.keys(data.errors ?? {}).length > 0) {
      message = 'Validierungsfehler: Bitte überprüfen Sie Ihre Eingaben.';
    }
    if (!message) {
      message = data?.title || `HTTP ${response.status}: ${response.statusText}`;
    }

    const error = new Error(message);
    error.status = response.status;
    error.statusText = response.statusText;
    error.data = data;
    throw error;
  }

  // Blob-Response direkt zurückgeben
  if (responseType === 'blob') {
    return { success: true, blob: await response.blob(), status: response.status };
  }

  // Bei 201 (Created) oder 204 (No Content) gibt es keinen Body
  if (response.status === 201 || response.status === 204) {
    return { success: true, status: response.status };
  }

  // Versuche JSON zu parsen
  const contentType = response.headers.get('content-type');
  let data = null;
  if (contentType && (contentType.includes('application/json') || contentType.includes('application/problem+json'))) {
    try {
      data = await response.json();
      console.log('API Response Data:', data);
    } catch (parseError) {
      console.error('Fehler beim Parsen der JSON-Response:', parseError);
    }
  } else {
    console.warn('Response hat keinen JSON Content-Type:', contentType);
  }


  return { success: true, data, status: response.status };
};

/**
 * Startet den XML-Import-Job
 * @param {File} file - Die XML-Datei
 * @returns {Promise<Object>} - ImportJobResponse
 */
export const uploadXmlImport = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return await fetchAPI('/refireg/import/xml', {
    method: 'POST',
    body: formData,
    // fetchAPI wird Authorization-Header automatisch hinzufügen
    // Content-Type muss bei FormData weggelassen werden, damit fetch den richtigen Boundary setzt
  });
};

/**
 * Ruft den Status eines XML-Import-Jobs ab
 * @param {string} jobId - UUID des Jobs
 * @returns {Promise<Object>} - ImportJobStatus
 */
export const getImportJobStatus = async (jobId) => {
  return await fetchAPI(`/refireg/import/xml/${jobId}/status`);
};

/**
 * Ruft die Liste aller bisherigen XML-Import-Jobs ab
 * @returns {Promise<Object>} - Array von ImportJobStatus-Objekten
 */
export const getImportJobs = async () => {
  return await fetchAPI('/refireg/import/xml');
};

/**
 * Erstellt eine neue Abteilung (Transaktion) im Refinanzierungsregister
 *
 * @param {Object} abteilungData - Die Abteilungsdaten
 * @param {string} abteilungData.registerfuehrendesUnternehmen - Name des registerführenden Unternehmens
 * Hinweis: `nummer` wird serverseitig fortlaufend vergeben.
 * @param {string} abteilungData.transaktionsbezeichner - Bezeichnung der Transaktion (optional)
 * @param {Array} abteilungData.registereintraege - Array von Registereinträgen (PersoenlicheForderung, Grundpfandrecht, Loeschung)
 * @returns {Promise<Object>} Response mit success-Status
 */
export const createAbteilung = async (abteilungData) => {
  return await fetchAPI('/refireg/abteilung', {
    method: 'POST',
    body: JSON.stringify(abteilungData),
  });
};

/**
 * Holt alle Abteilungen (Registerübersicht) aus dem Backend
 * @returns {Promise<Array>} Array von Abteilung-Objekten
 */
export const getAbteilungen = async () => {
  const response = await fetchAPI('/refireg/abteilung', {
    method: 'GET'
  });
  return response.data;
};

// UI-optimierte Endpunkte
export const getAbteilungenOverview = async () => {
  const response = await fetchAPI('/refireg/ui/abteilung/overview', { method: 'GET' });
  return response.data;
};

export const getAbteilungById = async (id) => {
  const response = await fetchAPI(`/refireg/ui/abteilung/${id}`, { method: 'GET' });
  return response.data;
};

/**
 * Holt die Audit Logs vom Backend
 * @returns {Promise<Array>} Array von AuditLog-Objekten
 */
export const getAuditLogs = async () => {
  const response = await fetchAPI('/refireg/ui/audit-log', { method: 'GET' });
  return response.data;
};

/**
 * Löscht einen einzelnen Registereintrag aus einer Abteilung (ID-basiert)
 *
 * @param {number} abteilungId - Die ID der Abteilung
 * @param {number} registereintragId - Die ID des Registereintrags
 * @param loeschungData
 * @returns {Promise<Object>} Response mit success-Status
 */
export const deleteRegistereintrag = async (abteilungId, registereintragId, loeschungData) => {
  return await fetchAPI(`/refireg/abteilung/${abteilungId}/registereintrag/${registereintragId}`, {
    method: 'DELETE',
    body: JSON.stringify(loeschungData),
  });
};

/**
 * Lädt den Refinanzierungsregisterauszug einer Abteilung als PDF herunter
 *
 * @param {string} abteilungId - Die ID der Abteilung (UUID)
 * @param {string} dateiname - Optionaler Dateiname für den Download (ohne .pdf)
 * @returns {Promise<void>}
 */
export const downloadAbteilungPdf = async (abteilungId, dateiname) => {
  const { blob } = await fetchAPI(`/refireg/abteilung/${abteilungId}/pdf`, {
    method: 'GET',
    responseType: 'blob',
  });

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = dateiname ? `${dateiname}.pdf` : `abteilung-${abteilungId}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

/**
 * Aktualisiert einen einzelnen Registereintrag in einer Abteilung (ID-basiert)
 *
 * @param {number} abteilungId - Die ID der Abteilung
 * @param {number} registereintragId - Die ID des Registereintrags
 * @param updateData - Die zu aktualisierenden Daten (z.B. betrag, bemerkung)
 * @returns {Promise<Object>} Response mit success-Status
 */
export const updateRegistereintrag = async (abteilungId, registereintragId, updateData) => {
  return await fetchAPI(`/refireg/abteilung/${abteilungId}/registereintrag/${registereintragId}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });
};

/**
 * Hilfsfunktion: Konvertiert Formular-Grundpfandrecht zu Backend-Format
 */
const safeTrim = (v) => (typeof v === 'string' ? v.trim() : '');

export const mapGrundpfandrechtToDTO = (grundpfandrecht, laufendeNr, rechtlicherGrund, datumRechtlicherGrund, uebertragungsberechtigter) => {
  const pfandobjekte = (grundpfandrecht.pfandobjekte || []).map(po => {
    const identifikationstyp = po.identifikationstyp || 'VerweisAufGrundbuchblatt';
    const ident = po.identifikation || {};

    let identifikation;
    if (identifikationstyp === 'VerweisAufGrundbuchblatt') {
      identifikation = {
        identifikationstyp: 'VerweisAufGrundbuchblatt',
        ortGrundbuch: safeTrim(ident.ortGrundbuch),
        amtsgericht: safeTrim(ident.amtsgericht),
        strasse: safeTrim(ident.strasse),
        hausNr: safeTrim(ident.hausNr),
        plz: safeTrim(ident.plz),
        ort: safeTrim(ident.ort),
        blattNr: safeTrim(ident.blattNr)
      };

      // Band ist optional
      if (safeTrim(ident.band)) {
        identifikation.band = safeTrim(ident.band);
      }
    } else if (identifikationstyp === 'BezeichnungImBestandsverzeichnis') {
      identifikation = {
        identifikationstyp: 'BezeichnungImBestandsverzeichnis',
        eintraege: [{
          lfdNrImBestandsverzeichnis: parseInt(safeTrim(ident.lfdNrImBestandsverzeichnis)) || 1,
          gemarkung: safeTrim(ident.gemarkung),
          flur: safeTrim(ident.flur),
          flurstueck: safeTrim(ident.flurstueck)
        }]
      };
    }

    return {
      identifikation,
      abteilungGrundbuch: safeTrim(po.abteilungGrundbuch) || 'III',
      lfdNrAbteilungGrundbuch: parseInt(safeTrim(po.lfdNrAbteilungGrundbuch)) || 1
    };
  });

  const betragText = safeTrim(grundpfandrecht.betrag);
  const umfangText = safeTrim(grundpfandrecht.umfang);
  const aktenzeichen = safeTrim(grundpfandrecht.aktenzeichen);

  return {
    type: 'Grundpfandrecht',
    laufendeNr: null,
    zeitpunktEintragung: new Date().toISOString(),
    aktenzeichen: aktenzeichen,
    uebertragungsberechtigter: uebertragungsberechtigter,
    betragText,
    umfangText,
    waehrung: 'EUR',
    rechtlicherGrund: rechtlicherGrund || 'Grundschuldbestellung',
    datumRechtlicherGrund: datumRechtlicherGrund || new Date().toISOString().split('T')[0],
    pfandobjekte
  };
};

/**
 * Hilfsfunktion: Erstellt Übertragungsberechtigten-Objekt
 */
export const mapUebertragungsberechtigterToDTO = (berechtigter) => {
  return {
    name: safeTrim(berechtigter.name) || 'DZ HYP AG',
    strasse: safeTrim(berechtigter.strasse) || 'Rosenstraße',
    hausNr: safeTrim(berechtigter.hausnummer) || '2',
    plz: safeTrim(berechtigter.postleitzahl) || '20009',
    ort: safeTrim(berechtigter.ort) || 'Hamburg'
  };
};

/**
 * Hilfsfunktion: Konvertiert Formular-PersoenlicheForderung zu Backend-Format
 */
export const mapPersoenlicheForderungToDTO = (forderung, laufendeNr, uebertragungsberechtigter) => {
  const aktenzeichen = safeTrim(forderung.aktenzeichen);

  return {
    type: 'PersoenlicheForderung',
    laufendeNr: null, // read only für clients; wird serverseitig automatisch gesetz
    zeitpunktEintragung: new Date().toISOString(),
    aktenzeichen: aktenzeichen,
    uebertragungsberechtigter: uebertragungsberechtigter,
    nameSchuldner: safeTrim(forderung.nameSchuldner),
    vornameSchuldner: safeTrim(forderung.vornameSchuldner) || null,
    waehrung: safeTrim(forderung.waehrung) || 'EUR',
    betrag: parseCurrencyInput(forderung.betrag),
    vorgangsNr: safeTrim(forderung.vorgangsNr)
  };
};

/**
 * Transformiert Formulardaten in das Backend-Format
 */
export const transformFormDataToAbteilung = (formData, grundpfandrechte, uebertragungsberechtigte, persoenlicheForderungen = []) => {
  // Erstelle das Übertragungsberechtigten-Objekt (nehme den ersten oder Standard)
  const uebertragungsberechtigterDTO = uebertragungsberechtigte.length > 0
    ? mapUebertragungsberechtigterToDTO(uebertragungsberechtigte[0])
    : {
        name: 'DZ HYP AG',
        strasse: 'Rosenstraße',
        hausNr: '2',
        plz: '20009',
        ort: 'Hamburg'
      };

  // Konvertiere persönliche Forderungen zu Registereinträgen –
  // nur Forderungen, bei denen ALLE Pflichtfelder ausgefüllt sind, werden aufgenommen
  const aktiveForderungen = persoenlicheForderungen.filter(f =>
    (f.nameSchuldner || '').trim() !== '' &&
    (f.betrag || '').toString().trim() !== '' &&
    (f.vorgangsNr || '').trim() !== ''
  );

  const forderungRegistereintraege = aktiveForderungen.map((forderung, index) =>
    mapPersoenlicheForderungToDTO(
      forderung,
      index + 1,
      uebertragungsberechtigterDTO
    )
  );

  // Konvertiere Grundpfandrechte zu Registereinträgen –
  // nur Grundpfandrechte, bei denen ALLE Pflichtfelder ausgefüllt sind, werden aufgenommen
  const aktiveGrundpfandrechte = grundpfandrechte.filter(gp => {
    const hasMandatoryGpFields =
      (gp.aktenzeichen || '').trim() !== '' &&
      (gp.betrag || '').toString().trim() !== '' &&
      (gp.umfang || '').toString().trim() !== '' &&
      (gp.rechtlicherGrund || '').trim() !== '' &&
      (gp.datumRechtlicherGrund || '').trim() !== '';

    if (!hasMandatoryGpFields) return false;

    return (gp.pfandobjekte || []).every(po => {
      // Gemeinsame Pflichtfelder
      const hasCommonFields =
        (po.abteilungGrundbuch || '').trim() !== '' &&
        (po.lfdNrAbteilungGrundbuch || '').toString().trim() !== '';

      if (!hasCommonFields) return false;

      const identifikationstyp = po.identifikationstyp || 'VerweisAufGrundbuchblatt';
      const ident = po.identifikation || {};

      if (identifikationstyp === 'VerweisAufGrundbuchblatt') {
        return (ident.ortGrundbuch || '').trim() !== '' &&
          (ident.amtsgericht || '').trim() !== '' &&
          (ident.blattNr || '').trim() !== '' &&
          (ident.strasse || '').trim() !== '' &&
          (ident.hausNr || '').trim() !== '' &&
          (ident.plz || '').trim() !== '' &&
          (ident.ort || '').trim() !== '';
        // band ist optional
      } else if (identifikationstyp === 'BezeichnungImBestandsverzeichnis') {
        return (ident.lfdNrImBestandsverzeichnis || '').toString().trim() !== '' &&
          (ident.gemarkung || '').trim() !== '' &&
          (ident.flur || '').trim() !== '' &&
          (ident.flurstueck || '').trim() !== '';
      }

      return false;
    });
  });

  const grundpfandrechtRegistereintraege = aktiveGrundpfandrechte.map((gp, index) =>
    mapGrundpfandrechtToDTO(
      gp,
      forderungRegistereintraege.length + index + 1,
      safeTrim(gp.rechtlicherGrund),
      safeTrim(gp.datumRechtlicherGrund),
      uebertragungsberechtigterDTO
    )
  );

  // Kombiniere alle Registereinträge
  const registereintraege = [...forderungRegistereintraege, ...grundpfandrechtRegistereintraege];

  // Erstelle Abteilung-Objekt gemäß Backend-Schema
  return {
    registerfuehrendesUnternehmen: 'DZ HYP AG',
    transaktionsbezeichner: formData.transaktion,
    registereintraege: registereintraege
  };
};
